/**
 * Hook for getting user role level
 * Returns the numeric role level (2, 4, 5, 6, or 8) for the current user
 */

import { useState, useEffect, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { DebugUserLevelContext } from "@/contexts/DebugUserLevelContext";

/**
 * Hook to get the current user's role level
 * Returns the numeric role level: 2 (user), 4 (paid), 5 (corporate), 6 (admin), or 8 (super_admin)
 * Defaults to level 2 (user) if role cannot be determined or user is not authenticated
 */
export const useUserRoleLevel = () => {
  // Get override level from context if available (for debug mode)
  const debugContext = useContext(DebugUserLevelContext);
  const overrideLevel = debugContext?.overrideLevel ?? null;
  
  const [roleLevel, setRoleLevel] = useState<number>(2);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  /**
   * Fetches the user's role level from the database
   * Uses the get_user_role_level database function
   */
  const fetchRoleLevel = async (userId: string): Promise<number> => {
    try {
      const { data, error } = await supabase.rpc('get_user_role_level', {
        _user_id: userId
      });

      if (error) {
        console.error('Error fetching user role level:', error);
        return 2; // Default to level 2 (user) on error
      }

      // Ensure we return a valid number, defaulting to 2 if null/undefined
      return typeof data === 'number' ? data : 2;
    } catch (error) {
      console.error('Unexpected error fetching user role level:', error);
      return 2; // Default to level 2 (user) on error
    }
  };

  useEffect(() => {
    let isMounted = true;

    /**
     * Initial fetch of user and role level
     */
    const initRoleLevel = async () => {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        
        if (!isMounted) return;

        setUser(currentUser);

        if (currentUser) {
          const level = await fetchRoleLevel(currentUser.id);
          if (isMounted) {
            setRoleLevel(level);
          }
        } else {
          if (isMounted) {
            setRoleLevel(2); // Default to level 2 when not authenticated
          }
        }
      } catch (error) {
        console.error('Error initializing role level:', error);
        if (isMounted) {
          setRoleLevel(2);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initRoleLevel();

    /**
     * Subscribe to auth state changes to update role level when user logs in/out
     */
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          const level = await fetchRoleLevel(currentUser.id);
          if (isMounted) {
            setRoleLevel(level);
            setLoading(false);
          }
        } else {
          if (isMounted) {
            setRoleLevel(2); // Default to level 2 when not authenticated
            setLoading(false);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Return override level if set, otherwise return actual role level
  const finalRoleLevel = overrideLevel !== null ? overrideLevel : roleLevel;

  return { roleLevel: finalRoleLevel, loading, user };
};
